import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import draggable from 'vuedraggable'

import { countItemAppearance } from '@src/util/functions'
import { WorkoutCardSelect } from '@src/components/card/workout-card/workout-card-select/workout-card-select'
import { Input } from '@src/elements/input/input'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Link } from '@src/elements/link/link'
import WorkoutData from '@src/data/workout-data'
import { WorkoutsSelectorFilters } from '@src/state/store'
import { WorkoutService } from '@src/service/workout'

import { BOTTOM_SCROLL_THRESHOLD, CARDS_ON_BUILDER_PAGE, DRAG_COMMON_SETTING } from '@src/util/constants'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { Button } from '@src/elements/button/button'
import Member from '@src/data/member'
import { OptionGroup } from '@src/elements/select/option-group'
import { Option } from '@src/elements/select/option'
import { Select } from '@src/elements/select/select'
import { defaultWorkoutSelectorFilters } from '@src/state/dafaults'

@Component({
  components: {
    draggable: draggable
  },
  computed: {
    workouts: {
      get: function () {
        const state = this.$store.state
        if (state.workoutSelectorFilters) {
          const filterByName = (workout: WorkoutData) => {
            if (!state.workoutSelectorFilters.name || !state.workoutSelectorFilters.name.trim().length) {
              return true
            }
            return workout.name && state.workoutSelectorFilters.name && workout.name.toLowerCase().includes(state.workoutSelectorFilters.name.toLowerCase().trim())
          }
          const filterByType = (workout: WorkoutData) => workout.isWorkoutByType(state.workoutSelectorFilters.type)
          const filterByTags = (workout: WorkoutData, filterTags: Set<string>) => {
            if (filterTags.size === 0) {
              return true
            }
            // build a list with all tags in a workout
            let exerciseTags: string[] = []
            workout.workoutExercises.forEach(workoutExercise => {
              exerciseTags = [...exerciseTags, ...workoutExercise.exercise.getTagKeys()]
            })
            // each tag needs to be found in at least 1 exercise in the workout
            return Array.from(filterTags).every(filterTag => {
              return exerciseTags.indexOf(filterTag) >= 0
            })
          }
          return this.$store.state.new_training_plan.availableWorkouts.filter((w) => filterByTags(w, state.workoutSelectorFilters.tags || new Set([])) && filterByType(w) && filterByName(w))
        }

        return this.$store.state.new_training_plan.availableWorkouts || []
      },
      set: function (workouts) {
        this.$store.state.new_training_plan.availableWorkouts = workouts
      }
    },
    workoutFilters: {
      get: function () {
        return this.$store.state.workoutSelectorFilters || {}
      },
      set: function (filters) {
        this.$store.state.workoutSelectorFilters = filters
      }
    },
    selectedWorkouts () {
      let selectedWorkouts = []
      this.$store.state.new_training_plan.trainingDays.forEach(day => {
        if (day.workoutAsArray.length > 0) {
          selectedWorkouts = selectedWorkouts.concat(day.workoutAsArray[0])
        }
      })
      return selectedWorkouts
    },
    member () {
      return this.$store.state.trainingMember
    }
  }
})
export class WorkoutSelector extends Vue {

  protected workouts: WorkoutData[]

  protected selectedWorkouts: WorkoutData[]
  private workoutFilters: WorkoutsSelectorFilters
  private selectedTypes: Set<string> = new Set<string>([])
  private searchHasError: boolean = false

  private loading: boolean = true

  private dragWorkoutsOptions: any = {
    group: {
      name: 'workoutSelector',  // for example if there are two lists: A and B:
      pull: 'clone',            // pull: 'clone' - is used to copy the selected element from A and move it in B;
      put: false,               // put: true - if drag&drop cloned element fom B in A
      revertClone: true         // revert cloned element to initial position after moving to a another list.
    },
    ...DRAG_COMMON_SETTING,
    sort: false,
    chosenClass: 'dragged-exercise'
  }

  private service: WorkoutService = new WorkoutService()

  // needed for pagination
  private renderedWorkoutCards: WorkoutData[] = []
  private page: number = 0
  private member: Member | null

  constructor () {
    super()
  }

  mounted () {
    this.member ? this.loadWorkouts(this.member.id) : this.loadWorkouts()
  }

  loadWorkouts (memberId?) {
    this.loading = true
    return this.service.loadWorkouts(memberId)
      .then(workouts => {
        this.workouts = workouts ? workouts.sort(function (a, b) {
          return (b.platformUpdatedAt || b.createdAt).localeCompare(a.platformUpdatedAt || a.createdAt)
        }) : []
        this.page = 0
        this.renderedWorkoutCards = this.workouts.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE)
        this.scroll()
      })
      .finally(() => this.loading = false)
  }

  @Watch('workoutFilters')
  goToBeginning () {
    this.page = 0
    this.renderedWorkoutCards = this.workouts.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE)
    document.getElementById('workouts-selector')!.scrollTop = 0
  }

  scroll () {
    document.getElementById('workouts-selector')!.addEventListener('scroll', this.checkBottomOfPage)
  }

  checkBottomOfPage () {
    let bottomOfWindow = document.getElementById('workouts-selector')!.scrollTop
      + document.getElementById('workouts-selector')!.offsetHeight
      + BOTTOM_SCROLL_THRESHOLD
      >= document.getElementById('workouts-selector')!.scrollHeight

    // if bottom of loaded page, load more
    if (bottomOfWindow) {
      if (this.workouts.length > this.renderedWorkoutCards.length) {
        this.page++
        this.renderedWorkoutCards = this.renderedWorkoutCards.concat(this.workouts.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE))
      }
    }
  }

  filterWorkoutsByType (value) {
    if (this.selectedTypes.has(value)) {
      this.selectedTypes.delete(value)
    } else {
      this.selectedTypes.add(value)
    }
    let selectedType: string | undefined = undefined
    if (this.selectedTypes.size === 1) {
      // get single value of set
      selectedType = this.selectedTypes.values().next().value
    }
    this.$store.dispatch('filterWorkoutSelector', { type: selectedType })
  }

  clearAllTypes () {
    this.selectedTypes = new Set<string>([])
    this.$store.dispatch('filterWorkoutSelector', { type: undefined })
  }

  filterWorkoutsByName (value: string) {
    this.$store.dispatch('filterWorkoutSelector', { name: value })
  }

  clearFilters () {
    this.$store.dispatch('filterWorkoutSelector', defaultWorkoutSelectorFilters())
  }

  filterByType (value) {
    this.$store.dispatch('filterWorkoutSelector', { type: (value === 'all') ? undefined : value })
  }

  render (h: CreateElement) {
    let typeFilterLabel = this.$t(`PAGE.CONTENT.WORKOUTS.TYPES.${{
      undefined: 'ALL',
      'certified': 'CERTIFIED_WORKOUTS',
      'non-certified': 'NON_CERTIFIED_WORKOUTS',
      'member-trainer': 'MEMBER_WORKOUTS'
    }[`${this.workoutFilters.type}`]}`)

    let workouts = this.renderedWorkoutCards.map((item) => {
      let id = item.id || 0
      return <WorkoutCardSelect workout={item} counter={countItemAppearance(this.selectedWorkouts, id)} />
    })

    return <div class='selector'>
      <div class='filters'>
        {/*Hide home workouts*/}
        {/*<Filter tag={'type'}*/}
        {/*        filterList={WorkoutTypes}*/}
        {/*        placeholder={this.$t('FILTER')}*/}
        {/*        translation={'PAGE.CONTENT.WORKOUT.'}*/}
        {/*        selectedFilters={this.selectedTypes}*/}
        {/*        filterCallback={this.filterWorkoutsByType}*/}
        {/*        clearAllCallback={this.clearAllTypes} />*/}
        <Input ref='searchByName' class={'search' + (this.searchHasError ? ' error' : '')} search={true}
               valueChanged={this.filterWorkoutsByName}
               placeholder={this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.SEARCH_WORKOUTS')}
               value={this.workoutFilters.name} />

        <Select filter={true} ref='typeFilter' value={typeFilterLabel}>
          <OptionGroup>
            <Option type='single-option' value={'all'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.ALL')}
                    selected={this.workoutFilters.type === undefined} select={this.filterByType}
                    detail={this.$store.state.new_training_plan.availableWorkouts.filter((w) => w.isWorkoutByType('')).length.toString()} />
            <Option type='single-option' value={'certified'}
                    label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.CERTIFIED_WORKOUTS')}
                    selected={this.workoutFilters.type === 'certified'} select={this.filterByType}
                    detail={this.$store.state.new_training_plan.availableWorkouts.filter((w) => w.isWorkoutByType('certified')).length.toString()} />
            <Option type='single-option' value={'non-certified'}
                    label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.NON_CERTIFIED_WORKOUTS')}
                    selected={this.workoutFilters.type === 'non-certified'} select={this.filterByType}
                    detail={this.$store.state.new_training_plan.availableWorkouts.filter((w) => w.isWorkoutByType('non-certified')).length.toString()} />
            {this.member ? <Option type='single-option' value={'member-trainer'}
                                   label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.MEMBER_WORKOUTS')}
                                   selected={this.workoutFilters.type === 'member-trainer'}
                                   select={this.filterByType}
                                   detail={this.$store.state.new_training_plan.availableWorkouts.filter((w) => w.isWorkoutByType('member-trainer')).length.toString()} />
              : null}
          </OptionGroup>
        </Select>

        {this.workoutFilters.tags!.size > 0 ?
          <Button type='remove' class='clear-filters-btn' ref='clearFiltersBtn'
                  label={this.$t('PAGE.CONTENT.WORKOUTS.CLEAR_FILTERS')} click={this.clearFilters} /> : null}
      </div>
      <div class='workouts-wrapper' id='workouts-selector'>
        {this.loading ?
          <Loader /> :
          workouts.length ?
            <draggable class='workout-selector-list' v-model={this.workouts} options={this.dragWorkoutsOptions}>
              {workouts}
            </draggable>

            : <ErrorScreen icon='workout/no-exercises-found'
                           message={this.workoutFilters.name ? this.$t('PAGE.CONTENT.WORKOUTS.NOT_FOUND.FILTER.NAME', { query: this.workoutFilters.name }) : this.$t('PAGE.CONTENT.WORKOUTS.NO_WORKOUTS')}>
              {this.workoutFilters.name ? <Link variant='colored' align='center' click={this.clearFilters}
                                                label={this.$t('PAGE.CONTENT.WORKOUTS.RESET_FILTERS')} /> : null}
            </ErrorScreen>
        }
      </div>
    </div>
  }

  beforeDestroy () {
    this.clearFilters()
  }

  @Watch('member', { deep: true })
  updateMemberWorkouts (newMember, oldMember) {
    if (!newMember) return this.loadWorkouts()
    if ((!oldMember && newMember) || (oldMember.id !== newMember.id)) {
      return this.loadWorkouts(newMember.id)
    }
  }
}
